import React, { useState, useEffect, useRef } from 'react';
import { SimpleModal } from '@/components/SimpleModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/components/Toast';
import { etiquetaService } from '@/lib/etiquetaService';
import { formatCurrency } from '@/lib/utils';
import { Printer, Download, Usb, Search, Tag, Plus, Minus, Trash2, Eye } from 'lucide-react';

export const EtiquetaModal = ({ open, onClose, products = [] }) => {
  const toast = useToast();
  const canvasRef = useRef(null);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [printerType, setPrinterType] = useState('argox'); // 'argox' ou 'zpl'
  const [previewProduct, setPreviewProduct] = useState(null);
  const [printing, setPrinting] = useState(false);
  
  // Filtrar produtos pela busca
  const filteredProducts = products.filter(p => 
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.barcode?.includes(searchTerm) ||
    p.sku?.includes(searchTerm)
  );
  
  // Verificar se produto está selecionado
  const isSelected = (productId) => {
    return selectedProducts.some(sp => sp.product.id === productId);
  };
  
  // Adicionar produto à seleção
  const addProduct = (product) => {
    if (!isSelected(product.id)) {
      setSelectedProducts([...selectedProducts, { product, quantity: 1 }]);
    }
  };
  
  // Remover produto da seleção
  const removeProduct = (productId) => {
    setSelectedProducts(selectedProducts.filter(sp => sp.product.id !== productId));
  };
  
  // Alterar quantidade
  const updateQuantity = (productId, delta) => {
    setSelectedProducts(selectedProducts.map(sp => {
      if (sp.product.id === productId) {
        const newQty = Math.max(1, Math.min(100, sp.quantity + delta));
        return { ...sp, quantity: newQty };
      }
      return sp;
    }));
  };
  
  // Definir quantidade diretamente
  const setQuantity = (productId, qty) => {
    const quantity = Math.max(1, Math.min(100, parseInt(qty) || 1));
    setSelectedProducts(selectedProducts.map(sp => {
      if (sp.product.id === productId) {
        return { ...sp, quantity };
      }
      return sp;
    }));
  };
  
  // Selecionar/deselecionar todos
  const toggleSelectAll = () => {
    if (selectedProducts.length === filteredProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredProducts.map(p => ({ product: p, quantity: 1 })));
    }
  };
  
  // Gerar preview
  useEffect(() => {
    if (previewProduct && canvasRef.current) {
      etiquetaService.generatePreview(previewProduct, canvasRef.current);
    }
  }, [previewProduct]);
  
  // Total de etiquetas
  const totalLabels = selectedProducts.reduce((sum, sp) => sum + sp.quantity, 0);
  
  // Imprimir via USB (Web Serial API)
  const handlePrintUSB = async () => {
    if (selectedProducts.length === 0) {
      toast.warning('Selecione pelo menos um produto');
      return;
    }
    
    setPrinting(true);
    try {
      const data = etiquetaService.generateBatch(selectedProducts, printerType);
      await etiquetaService.printViaSerial(data);
      toast.success(`${totalLabels} etiqueta(s) enviada(s) para impressão!`);
      onClose();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setPrinting(false);
    }
  };
  
  // Download do arquivo de impressão
  const handleDownload = () => {
    if (selectedProducts.length === 0) {
      toast.warning('Selecione pelo menos um produto');
      return;
    }
    
    const data = etiquetaService.generateBatch(selectedProducts, printerType);
    const extension = printerType === 'zpl' ? 'zpl' : 'prn';
    etiquetaService.downloadPrintFile(data, `etiquetas_gondola.${extension}`);
    toast.success(`Arquivo com ${totalLabels} etiqueta(s) baixado!`);
  };
  
  return (
    <SimpleModal 
      open={open} 
      onClose={onClose} 
      title="Imprimir Etiquetas de Gôndola"
      className="max-w-4xl"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Coluna Esquerda - Seleção de Produtos */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar produto por nome, código ou SKU..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1"
              data-testid="search-product-input"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <Label className="text-sm text-slate-600">
              {filteredProducts.length} produto(s) encontrado(s)
            </Label>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={toggleSelectAll}
              data-testid="select-all-btn"
            >
              {selectedProducts.length === filteredProducts.length ? 'Desmarcar todos' : 'Selecionar todos'}
            </Button>
          </div>
          
          <ScrollArea className="h-[300px] border rounded-lg">
            <div className="p-2 space-y-1">
              {filteredProducts.map((product) => (
                <div 
                  key={product.id}
                  className={`flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer ${
                    isSelected(product.id) ? 'bg-emerald-50 border border-emerald-200' : ''
                  }`}
                  onClick={() => isSelected(product.id) ? removeProduct(product.id) : addProduct(product)}
                  data-testid={`product-item-${product.id}`}
                >
                  <Checkbox 
                    checked={isSelected(product.id)}
                    onCheckedChange={() => {}}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{product.name}</p>
                    <p className="text-xs text-slate-500">
                      {product.barcode || 'Sem código'} • {formatCurrency(product.price)}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewProduct(product);
                    }}
                    data-testid={`preview-btn-${product.id}`}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              
              {filteredProducts.length === 0 && (
                <p className="text-center text-slate-400 py-8">
                  Nenhum produto encontrado
                </p>
              )}
            </div>
          </ScrollArea>
        </div>
        
        {/* Coluna Direita - Produtos Selecionados e Preview */}
        <div className="space-y-4">
          {/* Tipo de Impressora */}
          <div className="space-y-2">
            <Label>Tipo de Impressora</Label>
            <div className="flex gap-2">
              <Button
                variant={printerType === 'argox' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPrinterType('argox')}
                className={printerType === 'argox' ? 'bg-slate-900' : ''}
                data-testid="printer-argox-btn"
              >
                Argox (PPLA)
              </Button>
              <Button
                variant={printerType === 'zpl' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPrinterType('zpl')}
                className={printerType === 'zpl' ? 'bg-slate-900' : ''}
                data-testid="printer-zpl-btn"
              >
                Elgin (ZPL)
              </Button>
            </div>
          </div>
          
          {/* Preview */}
          <div className="space-y-2">
            <Label>Preview da Etiqueta (50x30mm)</Label>
            <div className="border rounded-lg p-4 bg-slate-50 flex items-center justify-center">
              {previewProduct ? (
                <canvas 
                  ref={canvasRef} 
                  className="border shadow-sm bg-white"
                  style={{ maxWidth: '100%', height: 'auto' }}
                />
              ) : (
                <p className="text-slate-400 text-sm py-8">
                  Clique no ícone <Eye className="w-4 h-4 inline" /> para visualizar
                </p>
              )}
            </div>
          </div>
          
          {/* Produtos Selecionados com Quantidade */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Tag className="w-4 h-4" />
              Produtos Selecionados ({selectedProducts.length})
            </Label>
            <ScrollArea className="h-[150px] border rounded-lg">
              <div className="p-2 space-y-2">
                {selectedProducts.map(({ product, quantity }) => (
                  <div 
                    key={product.id}
                    className="flex items-center gap-2 p-2 bg-white rounded border"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{product.name}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => updateQuantity(product.id, -1)}
                        data-testid={`qty-minus-${product.id}`}
                      >
                        <Minus className="w-3 h-3" />
                      </Button>
                      <Input
                        type="number"
                        min="1"
                        max="100"
                        value={quantity}
                        onChange={(e) => setQuantity(product.id, e.target.value)}
                        className="w-14 h-7 text-center text-sm"
                        data-testid={`qty-input-${product.id}`}
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => updateQuantity(product.id, 1)}
                        data-testid={`qty-plus-${product.id}`}
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-rose-500 hover:text-rose-600"
                        onClick={() => removeProduct(product.id)}
                        data-testid={`remove-btn-${product.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
                
                {selectedProducts.length === 0 && (
                  <p className="text-center text-slate-400 py-4 text-sm">
                    Nenhum produto selecionado
                  </p>
                )}
              </div>
            </ScrollArea>
          </div>
          
          {/* Total e Botões */}
          <div className="border-t pt-4">
            <p className="text-lg font-semibold mb-3">
              Total: {totalLabels} etiqueta(s)
            </p>
            
            <div className="flex gap-2">
              <Button
                onClick={handlePrintUSB}
                disabled={printing || selectedProducts.length === 0}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                data-testid="print-usb-btn"
              >
                <Usb className="w-4 h-4 mr-2" />
                Imprimir (USB)
              </Button>
              <Button
                onClick={handleDownload}
                disabled={selectedProducts.length === 0}
                variant="outline"
                className="flex-1"
                data-testid="download-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Baixar Arquivo
              </Button>
            </div>
            
            <p className="text-xs text-slate-500 mt-2">
              USB: Requer Chrome/Edge com permissão de porta serial
            </p>
          </div>
        </div>
      </div>
    </SimpleModal>
  );
};

export default EtiquetaModal;
